/**
 * 밀리초 단위의 시간을 HH:MM:SS 또는 MM:SS 형식으로 변환합니다.
 * 1시간 미만일 경우 MM:SS 형식, 1시간 이상일 경우 HH:MM:SS 형식을 사용합니다.
 *
 * @param totalMilliseconds 총 시간(밀리초)
 * @returns 포맷된 시간 문자열 (예: "01:05:30" 또는 "55:30")
 */
export const convertMillisecondsToTimeFormat = (
  totalMilliseconds: number,
  {
    forceShowHours = false,
    hoursLength = 2,
  }: { forceShowHours?: boolean; hoursLength?: number } = {},
): string => {
  if (isNaN(totalMilliseconds) || totalMilliseconds < 0) {
    return '00:00'; // 유효하지 않은 입력 처리
  }

  // 밀리초를 초로 변환
  const totalSeconds = Math.floor(totalMilliseconds / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  if (hours > 0 || forceShowHours) {
    const formattedHours = String(hours).padStart(hoursLength, '0');
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  } else {
    return `${formattedMinutes}:${formattedSeconds}`;
  }
};

/**
 * 파일 크기 단위를 나타내는 열거형
 */
enum ByteUnit {
  Bytes = 'Bytes',
  KB = 'KB',
  MB = 'MB',
  GB = 'GB',
  TB = 'TB',
}

/**
 * 파일 크기(바이트)를 받아 사람이 읽기 쉬운 형식(KB, MB, GB, TB)으로 변환합니다.
 * KB 이상 단위에서는 항상 지정된 소수점 자릿수까지 표시합니다.
 *
 * @param {number} bytes - 변환할 파일 크기 (바이트 단위의 정수).
 * @param {number} [decimals=1] - 결과값에 표시할 소수점 이하 자릿수 (기본값: 1).
 * @returns {string} 포맷된 파일 크기 문자열 (예: "1.0 TB", "6.6 MB").
 */
export const formatBytes = (bytes: number, decimals = 1): string => {
  // 상수 정의
  const CONVERSION_BASE = 1000;
  const UNITS = Object.values(ByteUnit);

  // 입력값 유효성 검사 및 특수 케이스 처리
  if (bytes === 0) return '0 Bytes';
  if (!Number.isFinite(bytes) || bytes < 0) {
    console.error('Invalid input: bytes must be a non-negative finite number.');
    return 'Invalid size';
  }

  // 유효한 소수점 자릿수 확보
  const validDecimals = Math.max(0, decimals);

  // 적절한 단위 인덱스 계산
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(CONVERSION_BASE)),
    UNITS.length - 1,
  );

  // 값 계산
  const value = bytes / Math.pow(CONVERSION_BASE, unitIndex);

  // 단위에 따른 표시 형식 결정
  const formattedValue =
    unitIndex === 0
      ? Math.floor(value) // Bytes는 정수로 표시
      : value.toFixed(validDecimals); // KB 이상은 소수점 표시

  return `${formattedValue}${UNITS[unitIndex]}`;
};

/**
 * 초 단위의 시간을 밀리초 단위로 변환합니다.
 *
 * @param seconds 변환할 초 단위 시간
 * @returns 밀리초 단위로 변환된 시간
 */
export const convertSecondsToMilliseconds = (seconds: number): number => {
  return Math.round(seconds * 1000);
};

/**
 * ISO 8601 형식의 시간 지속 시간 문자열을 밀리초(milliseconds)로 변환합니다.
 * 예: "PT1H37M24S" -> 5844000
 *
 * @param {string} durationString - ISO 8601 형식의 시간 지속 시간 문자열 (예: "PT1H", "PT5M30S", "PT10.5S").
 * 날짜 부분(P...T 앞부분)은 지원하지 않습니다.
 * @returns {number} 총 지속 시간을 밀리초 단위로 반환합니다. 형식이 잘못되었거나 T가 없으면 0을 반환합니다.
 */
/**
 * 밀리초 단위의 시간을 "23h 59m 12s" 형식으로 변환합니다.
 * 시간이 0이면 생략하고, 분이 0이고 시간도 0이면 분도 생략합니다.
 *
 * @param ms 총 시간(밀리초)
 * @returns 포맷된 시간 문자열 (예: "23h 59m 12s", "5m 30s", "45s")
 */
export const formatDurationWithUnits = (ms: number): string => {
  if (isNaN(ms) || ms < 0) {
    return '0s';
  }

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(' ');
};

export const parseIso8601DurationToMillis = (
  durationString: string,
): number => {
  // 정규식 설명:
  // ^PT        : "PT"로 시작
  // (?:(\d+)H)? : 시간(H) 부분. 숫자를 그룹 1로 캡처. 선택 사항.
  // (?:(\d+)M)? : 분(M) 부분. 숫자를 그룹 2로 캡처. 선택 사항.
  // (?:(\d+(?:\.\d+)?)S)? : 초(S) 부분. 숫자(소수점 포함 가능)를 그룹 3으로 캡처. 선택 사항.
  // $          : 문자열 끝
  const regex = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/;

  const match = durationString.match(regex);

  if (!match) {
    // "PT"만 있거나 형식이 아예 다른 경우
    if (durationString === 'PT') return 0;
    console.error(
      'Invalid or unsupported ISO 8601 duration format:',
      durationString,
    );
    return 0;
  }

  // match 배열:
  // match[0]: 전체 매칭된 문자열 (예: "PT1H37M24S")
  // match[1]: 시간(H) 숫자 문자열 (없으면 undefined)
  // match[2]: 분(M) 숫자 문자열 (없으면 undefined)
  // match[3]: 초(S) 숫자 문자열 (없으면 undefined)

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseFloat(match[3] || '0'); // 초는 소수점 가능성이 있으므로 parseFloat 사용

  // 각 단위를 밀리초로 변환하여 합산
  return (hours * 60 * 60 + minutes * 60 + seconds) * 1000;
};

/**
 * MatchLabel chip / tooltip 표시용 1-line 포맷.
 *
 * 출력 예 (모든 필드 있을 때): "FIFA WC R16 · KOR vs POL · 2002-06-04"
 * 출력 예 (matchDate 누락): "FIFA WC R16 · KOR vs POL"
 * 출력 예 (homeShort/awayShort 누락): "FIFA WC R16 · 2002-06-04"
 * 모든 필드 누락 시 null — caller 가 렌더 skip.
 *
 * ai-api MatchLabel 5필드 (matchId, competitionLabel, homeShort, awayShort, matchDate) 와
 * 1:1 매핑. matchId 는 표시에 안 쓰임 (식별용).
 */
export const formatMatchLabel = (
  label:
    | {
        competitionLabel?: string | null;
        homeShort?: string | null;
        awayShort?: string | null;
        matchDate?: string | null;
      }
    | null
    | undefined,
): string | null => {
  if (!label) return null;
  const parts: string[] = [];
  if (label.competitionLabel) parts.push(label.competitionLabel);
  if (label.homeShort && label.awayShort) {
    parts.push(`${label.homeShort} vs ${label.awayShort}`);
  } else if (label.homeShort) {
    parts.push(label.homeShort);
  } else if (label.awayShort) {
    parts.push(label.awayShort);
  }
  if (label.matchDate) parts.push(label.matchDate);
  if (parts.length === 0) return null;
  return parts.join(' · ');
};
